/**
 * Activity View Component
 *
 * Shows a timeline of plan events parsed from log entries.
 */

import { useMemo } from "react";
import Avatar from "./Avatar";

interface LogEntry {
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}

interface ActivityItem {
  id: string;
  timestamp: Date;
  agent: string;
  agentType?: "C" | "S" | "A";
  action: string;
  target?: string;
  detail?: string;
  color: string;
  keyHash?: string;
}

interface ActivityViewProps {
  entries: LogEntry[];
  onTaskClick?: (taskId: string) => void;
}

export default function ActivityView({ entries, onTaskClick }: ActivityViewProps) {
  const activities = useMemo(() => parseActivities(entries), [entries]);

  if (activities.length === 0) {
    return (
      <div className="text-base-content/40 italic text-sm p-4">
        No activity yet
      </div>
    );
  }

  return (
    <div className="space-y-0.5 p-2">
      {activities.map((activity, index) => (
        <button
          key={activity.id}
          className="flex gap-3 py-2 px-2 rounded-lg hover:bg-base-200/50 transition-colors w-full text-left cursor-pointer focus:outline-none focus:bg-base-200/50"
          onClick={() => activity.target && onTaskClick?.(activity.target)}
        >
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <Avatar name={activity.agent} keyHash={activity.keyHash} size="md" />
            {index < activities.length - 1 && (
              <div className="w-px flex-1 bg-base-300 mt-1 min-h-[8px]"></div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="text-sm">
              <span className="font-medium">{activity.agent}</span>
              {" "}
              <span className={getActionColor(activity.action)}>{activity.action}</span>
              {activity.target && (
                <>
                  {" "}
                  <span className="font-semibold">{activity.target}</span>
                </>
              )}
            </div>
            {activity.detail && (
              <div className="text-xs text-base-content/60 mt-0.5">
                "{activity.detail}"
              </div>
            )}
            <div className="text-xs text-base-content/40 mt-0.5">
              {formatRelativeTime(activity.timestamp)}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}


function parseActivities(entries: LogEntry[]): ActivityItem[] {
  const activities: ActivityItem[] = [];

  for (const entry of entries) {
    const timestamp = new Date(entry.ts);
    const agent = entry.agent || "system";
    // Extract key hash from signature (e.g., "ed25519:abc123...")
    const keyHash = entry.sig?.startsWith("ed25519:") ? entry.sig : undefined;

    for (const form of entry.forms) {
      const activity = parseForm(form, agent, timestamp, entry.seq, keyHash);
      if (activity) {
        activities.push(activity);
      }
    }
  }

  // Sort by timestamp descending (newest first)
  return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

function parseForm(
  form: string,
  agent: string,
  timestamp: Date,
  seq: number,
  keyHash?: string
): ActivityItem | null {
  // Parse (claims "agent" :at "time" (given action-task))
  const claimsMatch = form.match(
    /\(claims\s+"([^"]+)"(?:\s+:at\s+"[^"]*")?(?:\s+:note\s+"([^"]*)")?\s+\(given\s+([a-z]+)-([a-zA-Z0-9_-]+)\)\)/
  );
  if (claimsMatch) {
    const claimAgent = claimsMatch[1];
    const note = claimsMatch[2];
    const action = claimsMatch[3];
    const target = claimsMatch[4];

    return {
      id: `${seq}-${action}-${target}`,
      timestamp,
      agent: claimAgent,
      agentType: getAgentType(claimAgent),
      action: action,
      target: target,
      detail: note,
      color: getAgentColor(claimAgent),
      keyHash,
    };
  }

  // Parse (given completed-task)
  const completedMatch = form.match(/\(given\s+completed-([a-zA-Z0-9_-]+)\)/);
  if (completedMatch) {
    return {
      id: `${seq}-completed-${completedMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "completed",
      target: completedMatch[1],
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (given claimed-task)
  const claimedMatch = form.match(/\(given\s+claimed-([a-zA-Z0-9_-]+)\)/);
  if (claimedMatch && !form.includes("claims")) {
    return {
      id: `${seq}-claimed-${claimedMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "claimed",
      target: claimedMatch[1],
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (given blocked-task)
  const blockedMatch = form.match(/\(given\s+blocked-([a-zA-Z0-9_-]+)\)/);
  if (blockedMatch && !form.includes("claims")) {
    return {
      id: `${seq}-blocked-${blockedMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "blocked",
      target: blockedMatch[1],
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (given progress-task-N)
  const progressMatch = form.match(/\(given\s+progress-([a-zA-Z0-9_-]+)-(\d+)\)/);
  if (progressMatch) {
    return {
      id: `${seq}-progress-${progressMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "updated progress on",
      target: progressMatch[1],
      detail: `${progressMatch[2]}% complete`,
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (given task-X) - task creation
  const taskMatch = form.match(/\(given\s+task-([a-zA-Z0-9_-]+)\)/);
  if (taskMatch && agent === "system") {
    return {
      id: `${seq}-task-${taskMatch[1]}`,
      timestamp,
      agent: "Plan",
      action: "created task",
      target: taskMatch[1],
      color: "bg-base-content/30",
      keyHash,
    };
  }

  // Parse (normally rule-label condition conclusion) - defeasible rules
  const normallyMatch = form.match(/\(normally\s+([a-zA-Z0-9_-]+)\s+/);
  if (normallyMatch) {
    return {
      id: `${seq}-rule-${normallyMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "asserted rule",
      target: normallyMatch[1],
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (except label condition conclusion) - exception/defeater rules
  const exceptMatch = form.match(/\(except\s+([a-zA-Z0-9_-]+)\s+/);
  if (exceptMatch) {
    return {
      id: `${seq}-except-${exceptMatch[1]}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "asserted exception",
      target: exceptMatch[1],
      color: getAgentColor(agent),
      keyHash,
    };
  }

  // Parse (given X) - general facts (fallback for unmatched given forms)
  const givenMatch = form.match(/\(given\s+([a-zA-Z0-9_-]+)\)/);
  if (givenMatch) {
    const factName = givenMatch[1];
    return {
      id: `${seq}-fact-${factName}`,
      timestamp,
      agent,
      agentType: getAgentType(agent),
      action: "asserted fact",
      target: factName,
      color: getAgentColor(agent),
      keyHash,
    };
  }

  return null;
}

function getAgentType(agent: string): "C" | "S" | "A" | undefined {
  if (agent.includes("coder")) return "C";
  if (agent.includes("security")) return "S";
  if (agent.includes("architect")) return "A";
  return undefined;
}

function getAgentColor(agent: string): string {
  if (agent.includes("coder")) return "bg-secondary";
  if (agent.includes("security")) return "bg-accent";
  if (agent.includes("architect")) return "bg-primary";
  return "bg-base-content/30";
}

function getActionColor(action: string): string {
  switch (action) {
    case "completed":
      return "text-success";
    case "claimed":
      return "text-info";
    case "blocked":
      return "text-error";
    case "updated progress on":
      return "text-secondary";
    case "asserted rule":
      return "text-primary";
    case "asserted exception":
      return "text-warning";
    case "asserted fact":
      return "text-accent";
    default:
      return "text-base-content/70";
  }
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  return date.toLocaleDateString();
}
